/**
 * API URL Helper
 *
 * Safely constructs API URLs that work in both development and production.
 * Uses consolidated URL normalization logic from axios-auth.ts.
 * Handles same-origin detection to optimize URL construction.
 *
 * Environment Variable Semantics:
 *
 * REACT_APP_API_SERVER: The actual backend server URL (e.g., https://mypace-api.onrender.com)
 *   - Used for webpack proxy target in development
 *   - Should point directly to the backend server
 *   - Takes precedence over REACT_APP_API_URL
 *
 * REACT_APP_API_URL: The frontend-accessible API URL
 *   - May be same as REACT_APP_API_SERVER (cross-origin setup)
 *   - May be same as frontend origin (same-origin setup)
 *   - Used as fallback when REACT_APP_API_SERVER is not set
 *   - Used for client-side API calls
 */
import clientEnv from '../env';
import { normalizeBaseURL } from './axios-auth';
// Cache for base URL to avoid repeated calculations
let cachedBaseUrl = null;
let cacheKey = null;
/**
 * Get the base API URL for fetch requests
 *
 * In development: Returns relative path '/api' (works with webpack proxy)
 * In production: Returns relative path '/api' if app and API are on same origin,
 *                or constructs absolute URL if API is on different origin
 *
 * Results are cached to improve performance. Cache is invalidated when
 * environment variables or window.location changes.
 *
 * @returns {string} Base API URL (e.g., '/api' or 'https://mypace.app/api')
 */
export function getApiBaseUrl() {
    // Create cache key based on environment and current origin
    const currentCacheKey = typeof window !== 'undefined'
        ? `${clientEnv.NODE_ENV}-${clientEnv.REACT_APP_API_SERVER || clientEnv.REACT_APP_API_URL || 'none'}-${window.location.origin}`
        : `${clientEnv.NODE_ENV}-${clientEnv.REACT_APP_API_SERVER || clientEnv.REACT_APP_API_URL || 'none'}-ssr`;
    // Return cached result if available and cache key matches
    if (cachedBaseUrl && cacheKey === currentCacheKey) {
        return cachedBaseUrl;
    }
    let result;
    // In development, always use relative path (works with webpack proxy)
    if (typeof window !== 'undefined' && clientEnv.NODE_ENV === 'development') {
        result = '/api';
    }
    else {
        // Get API URL from environment (REACT_APP_API_SERVER takes precedence)
        const apiUrl = clientEnv.REACT_APP_API_SERVER || clientEnv.REACT_APP_API_URL;
        // If no explicit API URL is set, use relative path (assumes same origin)
        if (!apiUrl) {
            result = '/api';
        }
        else if (apiUrl.startsWith('/')) {
            // If API URL is a relative path, normalize it
            result = normalizeBaseURL(apiUrl);
        }
        else if (typeof window !== 'undefined') {
            // If API URL is absolute, check if it's same origin as current page
            try {
                const apiUrlObj = new URL(apiUrl);
                const currentOrigin = window.location.origin;
                // If same origin, use relative path (more efficient, no CORS, no extra DNS lookup)
                if (apiUrlObj.origin === currentOrigin) {
                    result = '/api';
                }
                else {
                    // Different origin - use normalized absolute URL
                    result = normalizeBaseURL(apiUrl);
                }
            }
            catch (error) {
                // If URL parsing fails, fall back to relative path
                console.warn('Failed to parse REACT_APP_API_URL, using relative path:', error);
                result = '/api';
            }
        }
        else {
            // Server-side rendering or fallback - normalize the URL
            result = normalizeBaseURL(apiUrl);
        }
    }
    // Cache the result
    cachedBaseUrl = result;
    cacheKey = currentCacheKey;
    return result;
}
/**
 * Clear the cached base URL (useful for testing or when environment changes)
 */
export function clearApiBaseUrlCache() {
    cachedBaseUrl = null;
    cacheKey = null;
}
/**
 * Construct a full API URL from a path
 *
 * @param {string} path - API path (e.g., '/auth/login' or 'auth/login')
 * @returns {string} Full API URL
 */
export function getApiUrl(path) {
    const baseUrl = getApiBaseUrl();
    // Ensure path starts with /
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    // If baseUrl is absolute, construct full URL
    if (baseUrl.startsWith('http://') || baseUrl.startsWith('https://')) {
        return `${baseUrl}${normalizedPath}`;
    }
    // If baseUrl is relative, combine them
    // Remove trailing slash from baseUrl if present
    const cleanBaseUrl = baseUrl.replace(/\/+$/, '');
    return `${cleanBaseUrl}${normalizedPath}`;
}
